import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import UpgradeModal from "../components/UpgradeModal";
import BookingModal from "../components/BookingModal";
import { Star, MapPin, Briefcase, Heart, ChevronRight, Sparkles, Loader2, Lock, Languages as LanguagesIcon, Search, X } from "lucide-react";
import { LANG_CODES, langName } from "../i18n/languages";
import { i18nLocale } from "../lib/locale";

export default function Replacements() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const needId = params.get("need");
  const [reps, setReps] = useState([]);
  const [tier, setTier] = useState("free");
  const [limits, setLimits] = useState({});
  const [total, setTotal] = useState(0);
  const [aiResults, setAiResults] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [upgradeFor, setUpgradeFor] = useState(null); // 'starter' | 'pro' | null
  const [bookingFor, setBookingFor] = useState(null); // {id, name} candidate to propose mission to
  const [filterWeek, setFilterWeek] = useState(false);
  const [langFilter, setLangFilter] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const params2 = {};
      if (needId) params2.need_id = needId;
      if (filterWeek) params2.available_within = 7;
      if (langFilter.length) params2.languages = langFilter.join(",");
      const { data } = await api.get("/replacements", { params: params2 });
      const list = Array.isArray(data) ? data : data.results;
      setReps(list || []);
      setTier(data.tier || "free");
      setLimits(data.limits || {});
      setTotal(data.total || (list || []).length);
      setAiResults(null);
      if (needId && (data.limits || {}).ai_match === true) {
        setAiBusy(true);
        try {
          const { data: ai } = await api.post("/replacements/ai_match", { need_id: needId });
          setAiResults(ai.results);
        } catch (e) {
          // silently fall back to algo scoring
        } finally { setAiBusy(false); }
      }
    })();
  }, [needId, filterWeek, langFilter]);

  const runAi = async () => {
    if (!needId) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/replacements/ai_match", { need_id: needId });
      setAiResults(data.results);
    } catch (e) {
      alert("Échec de l'analyse IA: " + (e.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const toggleFav = async (id) => {
    await api.post(`/replacements/${id}/favorite`);
  };

  const displayed = (() => {
    const arr = aiResults || reps || [];
    const seen = new Set();
    return arr.filter((r) => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
  })();

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{t("replacements.title")}</h1>
              <p className="text-sm text-[#6B726A] mt-1">
                {aiResults ? t("replacements.ai_reranked") :
                  t("replacements.count", { count: displayed.length })}
                {needId ? " " + t("replacements.for_your_need") : ""}
              </p>
            </div>
            {needId && (
              <button onClick={runAi} disabled={aiBusy} data-testid="ai-match-button"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA] disabled:opacity-60 transition-colors">
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {aiResults ? t("replacements.ai_done") : t("replacements.ai_analyze")}
              </button>
            )}
          </div>

          {/* Quick filters */}
          <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="quick-filters">
            <button
              onClick={() => setFilterWeek((v) => !v)}
              data-testid="filter-available-week"
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filterWeek
                  ? "bg-[#1A8F4A] text-white border-[#1A8F4A] shadow"
                  : "bg-white text-[#1A4D2E] border-[#E2E5E0] hover:bg-[#E8F0EA]"
              }`}>
              <span className={`w-2 h-2 rounded-full ${filterWeek ? "bg-white" : "bg-[#1A8F4A]"}`} />
              {t("replacements.available_this_week")}
              {filterWeek && total > 0 && <span className="text-[10px] opacity-80">({total})</span>}
            </button>
            <LangFilter selected={langFilter} onChange={setLangFilter} locale={i18n.language} />
            {filterWeek && (
              <span className="text-[11px] text-[#6B726A] italic">
                {t("replacements.sorted_by_availability")}
              </span>
            )}
          </div>

          {(tier === "free" || tier === "starter") && total > displayed.length && (
            <div className="rp-card p-5 mb-5 bg-gradient-to-br from-[#E8F0EA] to-white border-[#1A4D2E]/15 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white grid place-items-center"><Sparkles className="w-5 h-5 text-[#1A4D2E]" /></div>
              <div className="flex-1">
                <div className="font-medium text-sm" style={{ fontFamily: "Outfit" }}>
                  {total - displayed.length} {t("replacements.more_profiles_locked", { defaultValue: "profil(s) supplémentaires verrouillés" })}
                </div>
                <div className="text-xs text-[#6B726A]">{t("replacements.upgrade_hint", { defaultValue: "Passez à Pro pour voir tous les profils et activer le matching IA complet." })}</div>
              </div>
              <button onClick={() => nav("/pricing")} className="rp-btn-primary text-sm" data-testid="upgrade-cta">
                {t("billing.upgrade", { defaultValue: "Passer Pro" })}
              </button>
            </div>
          )}
          {displayed.length === 0 ? (
            <div className="rp-card p-12 text-center text-sm text-[#6B726A]">
              {t("replacements.no_replacements")}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {displayed.map((r) => {
                const isLocked = r.locked === true;
                return (
                <div key={r.id} className={`rp-card p-6 relative ${isLocked ? "opacity-95" : ""}`} data-testid={`replacement-card-${r.id}`}>
                  {isLocked && (
                    <div className="absolute inset-0 backdrop-blur-[2px] bg-white/40 rounded-2xl z-10 flex flex-col items-center justify-center gap-2 cursor-pointer"
                      onClick={() => setUpgradeFor(r.tier_required || "starter")}
                      data-testid={`locked-overlay-${r.id}`}>
                      <div className="w-12 h-12 rounded-full bg-white shadow grid place-items-center">
                        <Lock className="w-5 h-5 text-[#1A4D2E]" />
                      </div>
                      <div className="text-xs font-medium text-[#1A4D2E] bg-white/90 px-3 py-1 rounded-full">
                        {t("replacements.locked_click", { defaultValue: "Cliquer pour débloquer" })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#E8F0EA] text-[#1A4D2E] grid place-items-center font-medium text-lg overflow-hidden">
                      {r.avatar ? (
                        <img
                          src={r.avatar.startsWith("http") ? r.avatar : `${process.env.REACT_APP_BACKEND_URL}${r.avatar}`}
                          alt={r.name}
                          className="w-full h-full object-cover"
                          data-testid={`avatar-img-${r.id}`}
                        />
                      ) : (
                        (r.name || "?")[0].toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium truncate">{r.name}</h3>
                        {r.next_available_slot && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#E6F4EB] text-[#1A8F4A] border border-[#A8D9B6]" data-testid={`avail-badge-${r.id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#1A8F4A] animate-pulse" />
                            Dispo {new Date(r.next_available_slot).toLocaleDateString(i18nLocale(), { day: "2-digit", month: "2-digit" })}
                          </span>
                        )}
                        {!isLocked && (
                          <button onClick={() => toggleFav(r.id)} className="p-1 text-[#6B726A] hover:text-[#E3655B]" data-testid={`fav-${r.id}`}>
                            <Heart className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-[#6B726A]">{r.professional_role || "Pharmacien"} • {t("replacements.experience_years", { count: r.experience_years || 0 })}</div>
                      {r.rating_avg > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-xs">
                          <Star className="w-3.5 h-3.5 fill-[#E5A937] text-[#E5A937]" />
                          <span className="font-medium">{r.rating_avg.toFixed(1)}</span>
                          <span className="text-[#6B726A]">({r.reviews_count} avis)</span>
                        </div>
                      )}
                      {r.distance_km != null && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-[#6B726A]">
                          <MapPin className="w-3 h-3" /> {t("replacements.km_away", { km: r.distance_km })}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {(r.final_score != null || r.compatibility != null) && (
                        <div className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${
                          (r.final_score ?? r.compatibility) >= 80 ? "bg-[#E8F0EA] text-[#1A4D2E]" :
                          (r.final_score ?? r.compatibility) >= 60 ? "bg-[#FBEFDA] text-[#C97A20]" :
                          "bg-[#F0F2EF] text-[#6B726A]"
                        }`}>
                          {r.final_score ?? r.compatibility}{t("replacements.compatible")}
                        </div>
                      )}
                      {r.ai_score != null && (
                        <div className="text-[10px] text-[#6B726A] mt-1 flex gap-1.5 justify-end items-center">
                          <Sparkles className="w-3 h-3 text-[#1A4D2E]" /> IA {r.ai_score} · Algo {r.algo_score}
                        </div>
                      )}
                      {!aiResults && r.score_breakdown && (
                        <div className="text-[10px] text-[#6B726A] mt-1 flex gap-1.5 justify-end">
                          <span>🧠 {r.score_breakdown.skills}</span>
                          <span>👤 {r.score_breakdown.role}</span>
                          {r.score_breakdown.distance != null && r.distance_km != null && (
                            <span>📍 {r.score_breakdown.distance}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {r.ai_reason && (
                    <div className="mb-3 text-xs text-[#1A4D2E] bg-[#E8F0EA] border border-[#1A4D2E]/15 rounded-xl p-2.5 leading-snug flex gap-2">
                      <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{r.ai_reason}</span>
                    </div>
                  )}
                  {r.weighted && !isLocked && (
                    <div className="mb-3 space-y-2" data-testid={`weighted-${r.id}`}>
                      {(() => {
                        const w = r.weighted;
                        const badge = {
                          excellent: { label: "Excellent match", bg: "#E8F0EA", fg: "#1A4D2E" },
                          very_good: { label: "Très bon match",  bg: "#E0F1E5", fg: "#2F855A" },
                          good:      { label: "Match pertinent", bg: "#FBEFDA", fg: "#C97A20" },
                          low:       { label: "Match faible",    bg: "#FFF4F3", fg: "#E3655B" },
                        }[w.category] || {};
                        return (
                          <>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: badge.bg, color: badge.fg }}
                                data-testid={`badge-${r.id}`}>
                                {badge.label} · {w.score}%
                              </span>
                              <div className="flex gap-1 text-[10px] text-[#6B726A]">
                                <span title="Critères forts (40%)">🟢 {w.breakdown.strong}</span>
                                <span title="Critères moyens (35%)">🟡 {w.breakdown.medium}</span>
                                <span title="Critères complémentaires (25%)">🔵 {w.breakdown.complementary}</span>
                              </div>
                            </div>
                            {(w.strong_points?.length > 0 || w.missing?.length > 0) && (
                              <div className="flex flex-wrap gap-1">
                                {w.strong_points.map((p) => (
                                  <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#E8F0EA] text-[#1A4D2E]">✓ {p}</span>
                                ))}
                                {w.missing.map((m) => (
                                  <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4F3] text-[#E3655B]">✗ {m}</span>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {r.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {r.skills.slice(0, 4).map((s) => (
                        <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-[#F0F2EF] text-[#6B726A]">{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => isLocked ? setUpgradeFor(r.tier_required || "starter") : nav(`/replacements/${r.id}${needId ? `?need=${needId}` : ""}`)}
                      data-testid={`view-profile-${r.id}`}
                      className="flex-1 border border-[#E2E5E0] text-[#1A4D2E] rounded-full py-2 text-sm font-medium hover:bg-[#F7F8F6]">
                      {isLocked ? t("replacements.unlock", { defaultValue: "Débloquer" }) : t("replacements.view_profile")}
                    </button>
                    {!isLocked && (
                      <button onClick={() => setBookingFor({ id: r.id, name: r.name })} data-testid={`propose-${r.id}`}
                        className="flex-1 bg-[#1A4D2E] text-white rounded-full py-2 text-sm font-medium hover:bg-[#143C24] flex items-center justify-center gap-1">
                        Proposer une mission <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      {upgradeFor && (
        <UpgradeModal
          requiredTier={upgradeFor}
          onClose={() => setUpgradeFor(null)}
          onUpgrade={() => { setUpgradeFor(null); nav("/pricing"); }}
        />
      )}
      <BookingModal
        open={!!bookingFor}
        onClose={() => setBookingFor(null)}
        candidate={bookingFor}
        onSaved={(b) => { if (b) nav("/bookings"); }}
      />
    </div>
  );
}

function LangFilter({ selected, onChange, locale }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const toggle = (c) =>
    onChange(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]);

  const filtered = LANG_CODES.filter((c) =>
    langName(c, locale).toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="relative" data-testid="lang-filter">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-filter-button"
        className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
          selected.length
            ? "bg-[#1A4D2E] text-white border-[#1A4D2E] shadow"
            : "bg-white text-[#1A4D2E] border-[#E2E5E0] hover:bg-[#E8F0EA]"
        }`}>
        <LanguagesIcon className="w-3.5 h-3.5" />
        {t("replacements.languages_filter")}{selected.length > 0 && ` (${selected.length})`}
      </button>
      {selected.length > 0 && (
        <button onClick={() => onChange([])} data-testid="lang-filter-clear"
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#E3655B] text-white grid place-items-center">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
      {open && (
        <div className="absolute z-40 mt-2 w-60 bg-white rounded-2xl border border-[#E2E5E0] shadow-xl overflow-hidden" data-testid="lang-filter-panel">
          <div className="p-2 border-b border-[#E2E5E0]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-[#6B726A]" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                data-testid="lang-filter-search" placeholder={t("replacements.search_language_ph")}
                className="w-full text-xs pl-8 pr-2 py-1.5 rounded-lg border border-[#E2E5E0] outline-none focus:border-[#1A4D2E]" />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.map((c) => (
              <button key={c} onClick={() => toggle(c)} data-testid={`lang-filter-opt-${c}`}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-[#F7F8F6] ${
                  selected.includes(c) ? "text-[#1A4D2E] font-semibold" : "text-[#1f2421]"
                }`}>
                {langName(c, locale)}
                {selected.includes(c) && <span className="w-1.5 h-1.5 rounded-full bg-[#1A4D2E]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
